/**
 * The report controls that live in the URL: range and segment.
 *
 * One hook, one address. A report's URL says what it shows (`range=30d`,
 * `range=cycle&cycle=<id>`, `range=custom&from=…&to=…`, `seg=queue`), so a
 * link, a snapshot and a print view all re-open the same question the same
 * way (roadmap principle: state lives in the URL).
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDegapProcessStore } from '../../../../stores/prioritization/degapProcessStore';
import {
  rangeFromParams,
  rangeToParams,
  resolveRange,
  type RangeCycle,
  type ReportRange,
  type ResolvedRange,
} from './reportRange';
import { segmentFromParam, type SegmentKey } from './reportSegment';

export interface ReportControls {
  range: ReportRange;
  resolved: ResolvedRange;
  setRange: (next: ReportRange) => void;
  segment: SegmentKey;
  setSegment: (next: SegmentKey) => void;
  cycles: RangeCycle[];
  /** The current controls as a query string (no leading `?`), for links that must carry them. */
  search: string;
}

export function controlsSearch(range: ReportRange, segment: SegmentKey): string {
  const params = rangeToParams(range, new URLSearchParams());
  if (segment !== 'none') params.set('seg', segment);
  return params.toString();
}

export function useReportControls(): ReportControls {
  const [searchParams, setSearchParams] = useSearchParams();
  const cycles = useDegapProcessStore((s) => s.cycles) as unknown as RangeCycle[];

  const range = useMemo(() => rangeFromParams(searchParams), [searchParams]);
  const segment = useMemo(() => segmentFromParam(searchParams.get('seg')), [searchParams]);
  const resolved = useMemo(() => resolveRange(range, cycles ?? []), [range, cycles]);

  const setRange = useCallback(
    (next: ReportRange) => {
      setSearchParams((prev) => rangeToParams(next, new URLSearchParams(prev)), { replace: true });
    },
    [setSearchParams],
  );

  const setSegment = useCallback(
    (next: SegmentKey) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'none') p.delete('seg'); else p.set('seg', next);
        return p;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const search = useMemo(() => controlsSearch(range, segment), [range, segment]);

  return { range, resolved, setRange, segment, setSegment, cycles: cycles ?? [], search };
}
